import { graphql } from 'react-relay';
import { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import type {
	Operation,
	NotificationAction,
	useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation as useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$data,
	useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutationVariables as useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$variables,
} from './__types__/useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation.ts';
const useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation = graphql`
	mutation useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation(
		$contentId: ID!
		$tasks: [IndividualInlineTaskNotificationInput]!
	) {
		createInlineTaskNotification(input: { contentId: $contentId, tasks: $tasks }) {
			contentId
			success
			errors {
				message
			}
		}
	}
`;
type CreateInlineTaskNotificationMutationProps = {
	contentId: string;
	setError: React.Dispatch<React.SetStateAction<Error | null>>;
	showDontNotifyFailedFlag: () => void;
};

export type UpdatedTaskItem = {
	operation: Operation;
	recipientAccountId: string;
	taskId: string;
	notificationAction: NotificationAction;
	recipientMentionLocalId: string;
};

type SendCreateInlineTaskNotificationsProps = {
	tasks?: UpdatedTaskItem[];
};

export const useCreateInlineTaskNotificationMutation = ({
	contentId,
	setError,
	showDontNotifyFailedFlag,
}: CreateInlineTaskNotificationMutationProps): {
	sendCreateInlineTaskNotifications: ({ tasks }: SendCreateInlineTaskNotificationsProps) => void;
	cancelInlineTaskNotifications: ({ tasks }: SendCreateInlineTaskNotificationsProps) => void;
	createTaskNotificationError: ApolloError | undefined;
} => {
	const [createInlineTaskNotificationMutation, { error: createTaskNotificationError }] =
		useMutationInterop<
			useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$data,
			useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation$variables
		>(useCreateInlineTaskNotificationMutationCreateInlineTaskNotificationMutation);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendCreateInlineTaskNotifications = useCallback(
		({ tasks }: SendCreateInlineTaskNotificationsProps): void => {
			const fireAnalyticsEvent = (action: 'succeeded' | 'failed') => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'mentionsNotifications',
						action,
						actionSubject: 'createInlineTaskMention',
						attributes: {
							contentId,
							mentions: tasks?.map((task) => ({
								taskId: task.taskId,
								mentionId: task.recipientMentionLocalId,
								mentionedUserId: task.recipientAccountId,
							})),
						},
					},
				}).fire();
			};

			// NCS draft syncs need to happen prior to mutation in order for
			// email content to show up. A 15 second buffer after user closes
			// the flag notification will give draft syncs a chance to happen.
			setTimeout(() => {
				createInlineTaskNotificationMutation({
					variables: { contentId, tasks: tasks ?? [] },
				})
					.then(() => {
						fireAnalyticsEvent('succeeded');
					})
					.catch((error: Error) => {
						fireAnalyticsEvent('failed');
						setError(error);
					});
			}, 15000);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentId, createAnalyticsEvent, createInlineTaskNotificationMutation],
	);

	const cancelInlineTaskNotifications = useCallback(
		({ tasks }: SendCreateInlineTaskNotificationsProps): void => {
			createInlineTaskNotificationMutation({
				variables: { contentId, tasks: tasks ?? [] },
			}).catch(() => {
				showDontNotifyFailedFlag();
			});
		},
		[contentId, createInlineTaskNotificationMutation, showDontNotifyFailedFlag],
	);

	return {
		sendCreateInlineTaskNotifications,
		cancelInlineTaskNotifications,
		createTaskNotificationError,
	};
};
